import {
    DATE_TYPES,
    SPEED_MULTIPLIER,
    GPS_COORDINATE_CONSTANT,
    NOTE_TYPE_CONSTANTS,
    STOP_NOTE_TYPE_CONSTANTS,
    SPEED_TO_DISTANCE_UNITS,
    TIME_WINDOW_CONSTANTS,
    TIME_WINDOW_CONSTANT_NAMES
} from '@/utils/constants';
import { isAValidCoordinate, isEmptyOrSpaces, camelCaseToSentenceCase } from '@/helpers';
import marked from 'marked';
import i18n from '@/i18n';
import moment from 'moment';
// eslint-disable-next-line import/prefer-default-export
export const GeneralMixin = {
    methods: {
        $_handleLoaderState(state, text = i18n.t('common.loadingText')) {
            const loader = {
                isLoading: state,
                loadingText: text
            };
            this.$store.commit('CHANGE_LOADING_STATE', loader, { root: true });
        },
        $_setDefaultBrokenImage(event) {
            event.target.src = this.$root.defaultBrokenImage;
        },
        $_validateCoordinate(coordinates) {
            return isAValidCoordinate(coordinates);
        },
        $_displayAddressName(stop) {
            if (isAValidCoordinate(stop.address)) {
                if (stop.name === undefined || isEmptyOrSpaces(stop.name)) {
                    return GPS_COORDINATE_CONSTANT;
                }

                return stop.name;
            }

            return stop.address;
        },
        $_displayContactName(stop) {
            if (stop.stopRef && stop.stopId) {
                return stop.contact != null ? stop.contact.name : ' - ';
            }
            return stop?.locationType;
        },
        $_isEmptyOrSpaces(text) {
            return isEmptyOrSpaces(text);
        },
        $_getFileIcon(file) {
            const fileExt = file.split('.')[1];
            switch (fileExt) {
                case 'xlsx':
                    return 'excel-icon';
                case 'xls':
                    return 'excel-icon';
                case 'docx':
                    return 'word-icon';
                case 'doc':
                    return 'word-icon';
                case 'pdf':
                    return 'pdf-icon';
                case 'mp4':
                    return 'youtube-icon';

                default:
                    return null;
            }
        },
        $_camelCaseToSentenceCase(text) {
            return camelCaseToSentenceCase(text);
        },
        $_compiledMarkdown(note) {
            return marked(note, { sanitize: true });
        },
        $_getBadgeColor(type) {
            const noteTypes = [...NOTE_TYPE_CONSTANTS, ...STOP_NOTE_TYPE_CONSTANTS];

            const result = noteTypes.find((noteType) => noteType.name === type);

            if (result) 
                return result.color;

            return 'pending';
        },
        $_notePhotoLoading(event, source) {
            // If the photo thumbnail couldn't be loaded, it's probably because the photo hasn't been processed by the Image Processor function.
            // It should be processed soon, so show a wait indicator instead, and try and load it again after 5 seconds.
            event.target.src = '/img/loading-ico-60px.gif';

            setTimeout(() => {
                event.target.src = source;
            }, 5000);
        },
        $_getAssetIcon(type) {
            switch (type) {
                case 'Personnel':
                    return 'person';
                case 'Vehicle':
                    return 'local_shipping';
                case 'Tracker':
                    return 'share_location';
                case 'Trailer':
                    return 'rv_hookup';
                default:
                    return 'work';
            }
        },
        $_getMaskingText(text) {
            return text.replace(text.substring(2, text.length - 2), '********');
        },
        $_handleColumnSwitch(data, key, storeKey) {
            this.tableColumnOptions.columns[key] = data;

            localStorage.setItem(storeKey, JSON.stringify(this.tableColumnOptions));
        },
        $_applyColumnSwitchDefaults(storeKey, applyDefaultKeys) {
            Object.keys(applyDefaultKeys).forEach((propName) => {
                if (
                    this.tableColumnKeyOptions[propName] !== undefined ||
                    this.tableColumnKeyOptions[propName] !== null
                ) {
                    this.tableColumnKeyOptions[propName] = applyDefaultKeys[propName];
                    [this.tableColumnOptions.columns[propName]] = this.tableColumnKeyOptions[propName];
                }
            });

            const savedTableColumnOptions = JSON.parse(localStorage.getItem(storeKey));
            if (
                savedTableColumnOptions === undefined ||
                savedTableColumnOptions === null ||
                savedTableColumnOptions.columns === undefined ||
                savedTableColumnOptions.columns === null
            ) {
                return;
            }

            Object.keys(this.tableColumnOptions.columns).forEach((propName) => {
                if (
                    savedTableColumnOptions.columns[propName] !== undefined ||
                    savedTableColumnOptions.columns[propName] !== null
                ) {
                    const item = this.tableColumnKeyOptions[propName].find(
                        (option) => option.key === savedTableColumnOptions.columns[propName].key
                    );
                    if (item !== undefined) {
                        this.tableColumnOptions.columns[propName] = item;
                    }
                }
            });
        },
        $_getUsedByLabel(usedBy) {
            if (usedBy) {
                return usedBy.length > 3 ? `${usedBy.splice(0, 3).join(',')}...(${usedBy.length})` : usedBy.join(',');
            }

            return '';
        },
        $_isLoadCapacityTypeEnabled(loadCapacityList, label) {
            return loadCapacityList.findIndex((l) => l.label === label) > -1;
        },
        $_displayLoadValue(loadCapacityList, load, label, showSymbol = true) {
            if (
                load &&
                Object.prototype.hasOwnProperty.call(load, label.toLowerCase()) &&
                load[label.toLowerCase()] !== null
            ) {
                const loadValue = load[label.toLowerCase()];

                if (!showSymbol) 
                    return loadValue;

                let { symbol } = loadCapacityList.find((l) => l.label === label);
                if (symbol === null) {
                    symbol = '';
                }

                return `${loadValue} ${symbol}`;
            }
            return '';
        },
        $_getLoadTotals(items, includeLabels) {
            const totals = {};
            if (!includeLabels?.length) {
                return totals;
            }

            for (let i = 0; i < includeLabels.length; i++) {
                totals[includeLabels[i]] = 0;
            }

            if (!items?.length) 
                return totals;

            for (let i = 0; i < items.length; i++) {
                const { load } = items[i];
                if (load) {
                    for (let j = 0; j < includeLabels.length; j++) {
                        const label = includeLabels[j];
                        if (
                            Object.prototype.hasOwnProperty.call(load, label.toLowerCase()) &&
                            load[label.toLowerCase()] !== null
                        ) {
                            const value = parseFloat(load[label.toLowerCase()]);

                            if (!Number.isNaN(value)) {
                                totals[label] += value;
                            }
                        }
                    }
                }
            }

            return totals;
        },
        $_isValidServiceAreaCutOffCombination(deliveryWindow, currentDayOfTheWeek) {
            const {
                cutOffType,
                cutOffTimeBeforeSlotValue,
                cutOffTimeBeforeSlotType,
                cutOffByTimeValue,
                cutOffByTimeType,
                cutOffByTimeNDaysPriorValue
            } = deliveryWindow;

            const dayOfWeek = currentDayOfTheWeek;

            const isValid = (value) => value !== null && value !== undefined;
            if (!isValid(cutOffType)) {
                return true;
            }
            if (cutOffType !== 0 && cutOffType !== 1) {
                return false;
            }

            if (cutOffType === 0) {
                if (
                    !isValid(cutOffTimeBeforeSlotValue) ||
                    !isValid(cutOffTimeBeforeSlotType) ||
                    cutOffTimeBeforeSlotValue <= 0 ||
                    cutOffTimeBeforeSlotType < 0 ||
                    cutOffTimeBeforeSlotType > 2
                ) {
                    return false;
                }
            }

            if (cutOffType === 1) {
                if (
                    !isValid(cutOffByTimeValue) ||
                    !isValid(cutOffByTimeType) ||
                    cutOffByTimeType < 0 ||
                    cutOffByTimeType > 4 ||
                    !isValid(dayOfWeek) ||
                    dayOfWeek < 0 ||
                    dayOfWeek > 6
                ) {
                    return false;
                }

                if (cutOffByTimeType === 4) {
                    if (!isValid(cutOffByTimeNDaysPriorValue) || cutOffByTimeNDaysPriorValue <= 0) {
                        return false;
                    }
                }
            }

            return true;
        },
        $_generateServiceAreaCutOffLabel(deliveryWindow, currentDayOfTheWeek) {
            const {
                cutOffType,
                cutOffTimeBeforeSlotValue,
                cutOffTimeBeforeSlotType,
                cutOffByTimeValue,
                cutOffByTimeType,
                cutOffByTimeNDaysPriorValue
            } = deliveryWindow;

            const dayOfWeek = currentDayOfTheWeek;
            const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

            const isValid = (value) => value !== null && value !== undefined;

            if (!isValid(cutOffType)) {
                return 'No cut off';
            }

            if (!this.$_isValidServiceAreaCutOffCombination(deliveryWindow, currentDayOfTheWeek)) {
                return 'Invalid options';
            }

            const formatTime12Hour = (time24) => {
                // eslint-disable-next-line
                let [hours, rawMinutes] = time24.split(':').map(Number);
                const minutes = String(rawMinutes).padStart(2, '0');
                const amPm = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12 || 12;
                return `${hours}:${minutes} ${amPm}`;
            };

            let label = 'Invalid options';

            if (cutOffType === 0) {
                const timeUnitsSingular = ['minute', 'hour', 'day'];
                const timeUnitsPlural = ['minutes', 'hours', 'days'];
                const timeUnit =
                    cutOffTimeBeforeSlotValue === 1
                        ? timeUnitsSingular[cutOffTimeBeforeSlotType]
                        : timeUnitsPlural[cutOffTimeBeforeSlotType];
                label = `${cutOffTimeBeforeSlotValue} ${timeUnit} before delivery window`;
            } else if (cutOffType === 1) {
                let dayLabel = '';

                if (cutOffByTimeType === 0) {
                    dayLabel = 'same day';
                } else if (cutOffByTimeType >= 1 && cutOffByTimeType <= 3) {
                    const targetDay = (dayOfWeek - cutOffByTimeType + 7) % 7;
                    const dayCount =
                        cutOffByTimeType === 1
                            ? `1 day prior (${daysOfWeek[targetDay]})`
                            : `${cutOffByTimeType} days prior (${daysOfWeek[targetDay]})`;
                    dayLabel = dayCount;
                } else if (cutOffByTimeType === 4) {
                    const adjustedDays = cutOffByTimeNDaysPriorValue % 7;
                    const targetDay = (dayOfWeek - adjustedDays + 7) % 7;
                    const dayCount =
                        cutOffByTimeNDaysPriorValue === 1
                            ? `1 day prior (${daysOfWeek[targetDay]})`
                            : `${cutOffByTimeNDaysPriorValue} days prior (${daysOfWeek[targetDay]})`;
                    dayLabel = dayCount;
                }

                const formattedTime = formatTime12Hour(cutOffByTimeValue);

                label = `${formattedTime} ${dayLabel}`;
            }

            return label;
        },
        $_isValid24HourTime(time) {
            const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

            return typeof time === 'string' && timeRegex.test(time);
        },
        $_isValid24HourTimeRange(fromTime, toTime) {
            if (!this.$_isValid24HourTime(fromTime) || !this.$_isValid24HourTime(toTime)) {
                return false;
            }

            const toMinutes = (time) => {
                const [hours, minutes] = time.split(':').map(Number);
                return hours * 60 + minutes;
            };

            return toMinutes(fromTime) <= toMinutes(toTime);
        },
        $_isValidDate(date) {
            const dateRegex = /^\d{4}-(?:0?[1-9]|1[0-2])-(?:0?[1-9]|[12]\d|3[01])$/;
            return typeof date === 'string' && dateRegex.test(date);
        },
        $_isValidDateRange(fromDate, toDate) {
            if (!this.$_isValidDate(fromDate) || !this.$_isValidDate(toDate)) {
                return false;
            }

            const normalizeDate = (dateStr) => {
                const [year, month, day] = dateStr.split('-').map(Number);
                return new Date(year, month - 1, day);
            };

            return normalizeDate(fromDate) <= normalizeDate(toDate);
        },
        $_getLastLocationUpdateIndicatorColor(dateTime) {
            if (!dateTime) 
                return 'gray';

            // Use UTC for both times to avoid timezone mismatch issues
            // when browser timezone differs from driver timezone
            const now = moment.utc();
            const time = moment.utc(dateTime);
            const duration = Math.ceil(moment.duration(now.diff(time)).as('minutes'));
            let color = '';

            switch (true) {
                case duration <= 2:
                    color = 'green';
                    break;
                case duration > 2 && duration <= 11:
                    color = 'orange';
                    break;
                default:
                    color = 'red';
            }

            return color;
        },
        $_issueRelatedEntityReference(entity) {
            if (entity.stopId) 
                return entity.stopRef;

            if (entity.shipmentId) 
                return entity.shipmentRef;

            if (entity.tripId) 
                return entity.tripRef;

            return null;
        },
        $_issueRelatedEntityType(entity) {
            if (entity.stopId) 
                return 'Stop';

            if (entity.shipmentId) 
                return 'Shipment';

            if (entity.tripId) 
                return 'Trip';

            return null;
        },
        $_getTime(appointmentTime, timeWindowStart, timeWindowEnd) {
            let stopTime = null;
            if (timeWindowStart != null && timeWindowEnd != null) {
                let hasMatch = false;

                TIME_WINDOW_CONSTANTS.some((x) => {
                    if (timeWindowStart === x.timeWindowStart && timeWindowEnd === x.timeWindowEnd) {
                        stopTime = x.name;
                        hasMatch = true;
                    }
                    return timeWindowStart === x.timeWindowStart && timeWindowEnd === x.timeWindowEnd;
                });

                if (!hasMatch) {
                    stopTime = TIME_WINDOW_CONSTANT_NAMES.CustomTimeWindow;
                }
            } else if (appointmentTime != null) {
                stopTime = this.$options.filters.timeFormat(appointmentTime, DATE_TYPES.standardTime, true); // convert to standard time
            } else {
                stopTime = 'None';
            }
            return stopTime;
        }
    },
    data() {
        return {
            DATE_TYPES,
            SPEED_MULTIPLIER,
            GPS_COORDINATE_CONSTANT,
            SPEED_TO_DISTANCE_UNITS
        };
    }
};
